import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'General Inquiry',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://readdy.ai/api/form/d5jevshugl990ce15400', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message
        }).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: 'General Inquiry', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 px-6 bg-zinc-950">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-6 py-2 border-2 border-cyan-400 rounded-full mb-6">
            <span className="text-cyan-400 text-sm font-bold tracking-widest uppercase">Get In Touch</span>
          </div>
          <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6">LET'S CONNECT</h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            For booking inquiries, collaborations, or just to say what's up, drop me a message below. I'll get back to you as soon as possible.
          </p>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-8">
          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm text-gray-400 uppercase tracking-wide mb-3">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full bg-transparent border-b-2 border-gray-700 focus:border-cyan-400 text-white text-lg py-3 outline-none transition-colors duration-300"
              placeholder="Your name"
            />
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm text-gray-400 uppercase tracking-wide mb-3">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="w-full bg-transparent border-b-2 border-gray-700 focus:border-cyan-400 text-white text-lg py-3 outline-none transition-colors duration-300"
              placeholder="your@email.com"
            />
          </div>

          {/* Subject Field */}
          <div>
            <label htmlFor="subject" className="block text-sm text-gray-400 uppercase tracking-wide mb-3">
              Subject
            </label>
            <select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="w-full bg-transparent border-b-2 border-gray-700 focus:border-cyan-400 text-white text-lg py-3 outline-none transition-colors duration-300 cursor-pointer"
            >
              <option value="General Inquiry" className="bg-zinc-900">General Inquiry</option>
              <option value="Booking Request" className="bg-zinc-900">Booking Request</option>
              <option value="Collaboration" className="bg-zinc-900">Collaboration</option>
              <option value="Press & Media" className="bg-zinc-900">Press & Media</option>
              <option value="Other" className="bg-zinc-900">Other</option>
            </select>
          </div>

          {/* Message Field */}
          <div>
            <label htmlFor="message" className="block text-sm text-gray-400 uppercase tracking-wide mb-3">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              maxLength={500}
              rows={6}
              className="w-full bg-transparent border-b-2 border-gray-700 focus:border-cyan-400 text-white text-lg py-3 outline-none transition-colors duration-300 resize-none"
              placeholder="Your message (max 500 characters)"
            />
            <div className="text-right text-sm text-gray-500 mt-2">
              {formData.message.length}/500
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-4 bg-gradient-to-r from-red-500 to-purple-600 text-white text-base font-bold rounded-full hover:shadow-2xl hover:shadow-red-500/50 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
          </button>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="text-center text-green-400 font-semibold">
              Message sent successfully! I'll get back to you soon.
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="text-center text-red-400 font-semibold">
              Something went wrong. Please try again.
            </div>
          )}
        </form>

        {/* Social Media Section */}
        <div className="mt-20 text-center">
          <h3 className="text-2xl font-bold text-white mb-8">FOLLOW</h3>
          <div className="flex items-center justify-center gap-6">
            <a
              href="https://www.instagram.com/tharealest"
              target="_blank"
              rel="noopener noreferrer"
              className="w-16 h-16 flex items-center justify-center border-2 border-gray-700 rounded-full text-gray-400 hover:border-red-500 hover:text-red-500 hover:bg-red-500/10 transition-all duration-300"
            >
              <i className="ri-instagram-line text-2xl"></i>
            </a>
            <a
              href="https://www.tiktok.com/@tharealestlastcircle"
              target="_blank"
              rel="noopener noreferrer"
              className="w-16 h-16 flex items-center justify-center border-2 border-gray-700 rounded-full text-gray-400 hover:border-red-500 hover:text-red-500 hover:bg-red-500/10 transition-all duration-300"
            >
              <i className="ri-tiktok-line text-2xl"></i>
            </a>
            <a
              href="https://www.youtube.com/@tharealest1088"
              target="_blank"
              rel="noopener noreferrer"
              className="w-16 h-16 flex items-center justify-center border-2 border-gray-700 rounded-full text-gray-400 hover:border-red-500 hover:text-red-500 hover:bg-red-500/10 transition-all duration-300"
            >
              <i className="ri-youtube-line text-2xl"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
